/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ColorProps } from '../utils/UNSAFE_interpolations/colors';
import { Ellipse } from './Ellipse';
import { Path } from './Path';
import { Polygon } from './Polygon';
import { Rect } from './Rect';
import { MarkerShapes } from '../utils/UNSAFE_visTypes/common';

type MarkerProps = {
  scale: number;
  type: MarkerShapes;
  height?: number;
  width?: number;
  fill: ColorProps['color'];
  stroke: ColorProps['color'];
  outerStroke?: ColorProps['color'];
  preserveRectAspectRatio?: boolean;
  tx?: number;
  ty?: number;
  isInteractive?: boolean;
  dataInfo?: Record<string, any>;
  outerStrokeWidth?: number;
  innerStrokeWidth?: number;
};

const GOLDEN_RATIO = 1.62;
const HUMAN_SCALE_X = 0.8; // preserve legacy human marker scale w/h = 0.8
const SELECTED_HUMAN_SCALE = 0.9;

export const MARKER_PADDING = 3;

export const Marker = ({
  scale,
  type,
  height = 100,
  width = 100,
  fill,
  stroke,
  outerStroke,
  tx,
  ty,
  preserveRectAspectRatio,
  isInteractive = false,
  dataInfo,
  outerStrokeWidth = MARKER_PADDING,
  innerStrokeWidth = 1
}: MarkerProps) => {
  const scaleX = type === 'human' ? scale * HUMAN_SCALE_X * SELECTED_HUMAN_SCALE : scale;
  const scaleY =
    type === 'rectangle' && preserveRectAspectRatio
      ? scale / GOLDEN_RATIO
      : type === 'human'
      ? scale * SELECTED_HUMAN_SCALE
      : scale;
  const tX = tx != null ? tx : ((1 - scaleX) * width) / 2;
  const tY = ty != null ? ty : ((1 - scaleY) * height) / 2;
  const ry = (height / 2) * (type === 'ellipse' && preserveRectAspectRatio ? 1 / GOLDEN_RATIO : 1);

  switch (type) {
    case 'human': {
      if (outerStroke) {
        return (
          <g>
            <Path
              type="human"
              tx={tX}
              ty={tY}
              fill={fill}
              stroke={outerStroke}
              strokeWidth={outerStrokeWidth}
              width={width}
              height={height}
              scaleX={scaleX}
              scaleY={scaleY}
            />
            <Path
              type="human"
              tx={tX}
              ty={tY}
              fill={fill}
              stroke={stroke}
              strokeWidth={innerStrokeWidth}
              width={width}
              height={height}
              scaleX={scaleX}
              scaleY={scaleY}
              isInteractive={isInteractive}
              dataInfo={dataInfo}
            />
          </g>
        );
      }
      return (
        <Path
          type="human"
          tx={tX}
          ty={tY}
          fill={fill}
          stroke={stroke}
          width={width}
          height={height}
          scaleX={scaleX}
          scaleY={scaleY}
          isInteractive={isInteractive}
          dataInfo={dataInfo}
        />
      );
    }
    case 'rectangle':
    case 'square': {
      const x = (-width / 2) * scaleX;
      const y = (-height / 2) * scaleY;

      if (outerStroke) {
        return (
          <g>
            <Rect
              x={x}
              y={y}
              width={width - outerStrokeWidth}
              height={height - outerStrokeWidth}
              scaleX={scaleX}
              scaleY={scaleY}
              fill={fill}
              stroke={outerStroke}
              strokeWidth={outerStrokeWidth}
              tx={tX + outerStrokeWidth / 2}
              ty={tY + outerStrokeWidth / 2}
            />
            <Rect
              x={x}
              y={y}
              width={width - outerStrokeWidth}
              height={height - outerStrokeWidth}
              scaleX={scaleX}
              scaleY={scaleY}
              fill={fill}
              stroke={stroke}
              strokeWidth={innerStrokeWidth}
              tx={tX + outerStrokeWidth / 2}
              ty={tY + outerStrokeWidth / 2}
              isInteractive={isInteractive}
              dataInfo={dataInfo}
            />
          </g>
        );
      }
      return (
        <Rect
          x={x}
          y={y}
          width={width - 2 * outerStrokeWidth}
          height={height - 2 * outerStrokeWidth}
          scaleX={scaleX}
          scaleY={scaleY}
          fill={fill}
          stroke={stroke}
          tx={tX + outerStrokeWidth}
          ty={tY + outerStrokeWidth}
          isInteractive={isInteractive}
          dataInfo={dataInfo}
        />
      );
    }
    case 'circle':
    case 'ellipse': {
      if (outerStroke) {
        return (
          <g>
            <Ellipse
              cx={0}
              cy={0}
              scaleX={scaleX}
              scaleY={scaleY}
              fill={fill}
              stroke={outerStroke}
              strokeWidth={outerStrokeWidth}
              rx={width / 2 - outerStrokeWidth}
              ry={ry - outerStrokeWidth}
              tx={tX}
              ty={tY}
              isInteractive={isInteractive}
              dataInfo={dataInfo}
            />
            <Ellipse
              cx={0}
              cy={0}
              scaleX={scaleX}
              scaleY={scaleY}
              fill={fill}
              stroke={stroke}
              strokeWidth={innerStrokeWidth}
              rx={width / 2 - outerStrokeWidth}
              ry={ry - outerStrokeWidth}
              tx={tX}
              ty={tY}
              isInteractive={isInteractive}
              dataInfo={dataInfo}
            />
          </g>
        );
      }
      return (
        <Ellipse
          cx={0}
          cy={0}
          scaleX={scaleX}
          scaleY={scaleY}
          fill={fill}
          stroke={stroke}
          rx={width / 2 - outerStrokeWidth}
          ry={ry - outerStrokeWidth}
          tx={tX}
          ty={tY}
          isInteractive={isInteractive}
          dataInfo={dataInfo}
        />
      );
    }
    default: {
      if (outerStroke) {
        return (
          <g>
            <Polygon
              fill={fill}
              stroke={outerStroke}
              strokeWidth={outerStrokeWidth}
              type={type}
              scaleX={scaleX}
              scaleY={scale}
              tx={tX}
              ty={tY}
              height={height - outerStrokeWidth}
              width={width - outerStrokeWidth}
              isInteractive={isInteractive}
              dataInfo={dataInfo}
            />
            <Polygon
              fill={fill}
              stroke={stroke}
              strokeWidth={innerStrokeWidth}
              type={type}
              scaleX={scaleX}
              scaleY={scale}
              tx={tX}
              ty={tY}
              height={height - outerStrokeWidth}
              width={width - outerStrokeWidth}
              isInteractive={isInteractive}
              dataInfo={dataInfo}
            />
          </g>
        );
      }
      return (
        <Polygon
          fill={fill}
          stroke={stroke}
          type={type}
          scaleX={scaleX}
          scaleY={scale}
          tx={tX}
          ty={tY}
          height={height - 2 * outerStrokeWidth}
          width={width - 2 * outerStrokeWidth}
          isInteractive={isInteractive}
          dataInfo={dataInfo}
        />
      );
    }
  }
};
